import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { useProductFetcher } from 'components/WorkspaceEditor/api/useProductFetcher/useProductFetcher'
import { useProductFetcherUtils } from 'components/WorkspaceEditor/api/useProductFetcher/useProductFetcher.utils'
import {
    useNotification,
    useTransactionManager,
} from 'components/WorkspaceEditor/services'
import { useSuggestionsShallowSelector } from 'components/WorkspaceEditor/SuggestionStore'
import {
    GetProjectNuggetContentViewResponse,
    GetProjectProductFamilyViewResponse,
} from 'components/WorkspaceEditor/types/types'
import { getRootArticleContentItemIds } from 'components/WorkspaceEditor/utils/contentItemViewNodesUtils'
import { useShallowSelector } from 'components/WorkspaceEditor/Workspace2Store'
import { TrackedChange } from 'models/changes'
import { useCallback, useRef } from 'react'
import { acceptRejectTrackChanges } from '../api'
import { useTreeMutationsWithTrackChanges } from '../services'
import { Action } from '../types/types'
import {
    getUpdatedContentItemViewNodesForNonSiblingsElements,
    handleProductFetcherCallback,
    processNonSiblingElementsForTrackChanges,
} from '../utils/TrackChangesPanelUtils'

// Safety timeout to automatically clear the track change processing flag if the operation does not complete in time.
// Prevents UI from getting stuck and allows users to retry accept/reject actions.
const TRACK_CHANGE_OPERATION_TIMEOUT_MS = 15000 // Safety timeout

export interface UpdatedTrackChangesData {
    trackChangesData: TrackedChange[]
    action: Action
    nonSiblingElements?: Record<string, Partial<TextContentElement>>
}

/**
 * Custom hook to handle card actions in the Track Changes panel.
 * Manages optimistic updates and fetches content item view nodes after actions.
 */
export const useHandleCardActions = () => {
    const { contentItemViewNodes } = useShallowSelector((state) => ({
        contentItemViewNodes: state.contentItemViewNodes,
    }))
    const { executeWithOptimistic } = useTransactionManager()
    const { onContentViewFetchSuccess } = useProductFetcherUtils({
        overrideContentItemNodes: false,
    })
    const notification = useNotification()
    const { acceptRejectTrackChangesData } = acceptRejectTrackChanges()
    const { updateTreeViewNodes } = useTreeMutationsWithTrackChanges()
    const {
        trackChangesData,
        setTrackChangeProcessing,
    } = useSuggestionsShallowSelector((state) => ({
        trackChangesData: state.trackChangesData,
        setTrackChangeProcessing: state.setTrackChangeProcessing,
    }))
    const trackChangesContextRef = useRef<UpdatedTrackChangesData | undefined>()
    const { getContentItemViewNodesWithApolloClient } = useProductFetcher({
        overrideContentItemNodes: false,
        callback: async (
            response:
                | GetProjectProductFamilyViewResponse
                | GetProjectNuggetContentViewResponse
        ) => {
            await handleProductFetcherCallback(
                trackChangesContextRef.current,
                response,
                onContentViewFetchSuccess,
                updateTreeViewNodes
            )
        },
    })

    // Handles optimistic UI update after a successful track change action
    const onSuccessOptimisticUpdate = useCallback(
        async (ids: string[], action: Action, resultData: TrackedChange[]) => {
            const originalContentItems = structuredClone(contentItemViewNodes)
            const nonSiblingElements = processNonSiblingElementsForTrackChanges(
                originalContentItems,
                ids
            )
            const updatedTcData: UpdatedTrackChangesData = {
                trackChangesData: resultData,
                action,
                nonSiblingElements,
            }
            trackChangesContextRef.current = updatedTcData

            const nonSiblingIds = Object.keys(nonSiblingElements)
            const filteredIds = ids.filter((id) => !nonSiblingIds.includes(id))

            // If only non-sibling elements, update tree and return early
            if (filteredIds.length === 0 && nonSiblingIds.length > 0) {
                const updatedContentItem = getUpdatedContentItemViewNodesForNonSiblingsElements(
                    originalContentItems,
                    nonSiblingElements,
                    action
                )
                updateTreeViewNodes(updatedContentItem, updatedTcData)
                return
            }

            // Otherwise, fetch content for filtered IDs
            const rootArticleContentItemIds = getRootArticleContentItemIds(
                originalContentItems,
                filteredIds
            )
            try {
                await getContentItemViewNodesWithApolloClient(
                    rootArticleContentItemIds
                )
            } catch (error) {
                console.error('Error fetching content item view nodes:', error)
                updateTreeViewNodes(originalContentItems, updatedTcData)
            }
        },
        [
            getContentItemViewNodesWithApolloClient,
            updateTreeViewNodes,
            contentItemViewNodes,
        ]
    )

    /**
     * Handles actions on suggestions in content items with optimistic UI and error fallback.
     */
    const handleSuggestionsInContentItem = useCallback(
        async (ids: string[], action: Action, trackChangesId?: string) => {
            // Block concurrent operations
            setTrackChangeProcessing(true)

            // Safety timeout
            const timeoutId = setTimeout(() => {
                console.warn(
                    'Track change operation timeout - clearing processing flag'
                )
                setTrackChangeProcessing(false)
            }, TRACK_CHANGE_OPERATION_TIMEOUT_MS)

            const backupContentItemViewNodes = [...contentItemViewNodes]
            let resultData: TrackedChange[] = []

            try {
                await executeWithOptimistic({
                    fallbackState: JSON.stringify(contentItemViewNodes),
                    asyncAction: async () => {
                        const result = await acceptRejectTrackChangesData(
                            trackChangesId as string,
                            action
                        )
                        resultData =
                            result?.data?.acceptTrackChange ||
                            result?.data?.rejectTrackChange ||
                            result?.data?.acceptAllTrackChanges ||
                            result?.data?.rejectAllTrackChanges ||
                            []
                    },
                    successCbFn: async () => {
                        return await onSuccessOptimisticUpdate(
                            ids,
                            action,
                            resultData
                        )
                    },
                    errorCbFn: () => {
                        updateTreeViewNodes(backupContentItemViewNodes)
                        console.error('Track changes operation failed:')
                        notification.error({
                            message:
                                'Failed to process track changes. Please try again.',
                        })
                        // Allow retry on error
                        setTrackChangeProcessing(false)
                    },
                })
            } catch (error) {
                console.error('Track changes operation failed:', error)
                // Allow retry on unexpected errors
                setTrackChangeProcessing(false)
            } finally {
                // Clean up timeout
                clearTimeout(timeoutId)
            }
        },
        [
            contentItemViewNodes,
            trackChangesData,
            executeWithOptimistic,
            acceptRejectTrackChangesData,
            onSuccessOptimisticUpdate,
            updateTreeViewNodes,
            setTrackChangeProcessing,
            notification,
        ]
    )

    return { handleSuggestionsInContentItem }
}
